import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Upload, Link as LinkIcon, Copy, Trash2, X, Loader2, ImageIcon, FileVideo, FileText } from "lucide-react";

/**
 * Media library modal.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (item) => void   item: { url, kind, name, ... }
 *  - kind?: "image" | "video" | "any"  (filter)
 */
const MediaLibraryModal = ({ open, onClose, onSelect, kind = "any" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("library");
  const [url, setUrl] = useState("");
  const [urlKind, setUrlKind] = useState("image");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  if (!open) return null;

  const filtered = kind === "any" ? items : items.filter((i) => i.kind === kind);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("تم الرفع");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onRegisterUrl = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    try {
      await api.post("/admin/media/register-url", { url: url.trim(), kind: urlKind, name: "" });
      toast.success("تمت الإضافة");
      setUrl("");
      setTab("library");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (it) => {
    if (!window.confirm("حذف الوسيط؟")) return;
    try {
      await api.delete(`/admin/media/${it.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("تم نسخ الرابط"); }
    catch { toast.error("تعذّر النسخ"); }
  };

  return (
    <div dir="rtl" className="fixed inset-0 z-[100] bg-ink/90 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-6" data-testid="media-modal">
      <div className="relative w-full max-w-5xl bg-ink-soft border border-white/15 max-h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ImageIcon size={16} className="text-gold" />
            <h3 className="font-arabic font-bold text-bone text-lg">المكتبة</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-bone" aria-label="إغلاق" data-testid="media-close">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10 px-3">
          <TabBtn active={tab === "library"} onClick={() => setTab("library")} testId="media-tab-library">المكتبة</TabBtn>
          <TabBtn active={tab === "upload"} onClick={() => setTab("upload")} testId="media-tab-upload">رفع جديد</TabBtn>
          <TabBtn active={tab === "url"} onClick={() => setTab("url")} testId="media-tab-url">من رابط</TabBtn>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {tab === "library" && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="animate-spin" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-white/50 font-arabic">لا توجد وسائط بعد. ابدأ بالرفع أو إضافة رابط.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filtered.map((it) => (
                    <div key={it.id} data-testid={`media-item-${it.id}`} className="group relative border border-white/10 hover:border-gold/50 transition-colors bg-ink overflow-hidden">
                      <button
                        type="button"
                        onClick={() => onSelect && onSelect(it)}
                        className="block w-full text-left cursor-pointer"
                        data-testid={`media-select-${it.id}`}
                      >
                        <div className="aspect-square bg-ink-soft flex items-center justify-center overflow-hidden">
                          {it.kind === "image" ? (
                            <img src={it.url} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          ) : it.kind === "video" ? (
                            <FileVideo size={36} className="text-white/40" />
                          ) : (
                            <FileText size={36} className="text-white/40" />
                          )}
                        </div>
                        <div className="px-3 py-2 text-xs text-white/60 truncate font-arabic">{it.name || "—"}</div>
                      </button>
                      <div className="absolute top-2 left-2 flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); copy(it.url); }} className="p-1.5 bg-ink/85 border border-white/15 text-white/80 hover:text-gold" title="نسخ"><Copy size={12} /></button>
                        <button onClick={(e) => { e.stopPropagation(); remove(it); }} className="p-1.5 bg-ink/85 border border-white/15 text-white/80 hover:text-red-400" title="حذف" data-testid={`media-delete-${it.id}`}><Trash2 size={12} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "upload" && (
            <label className="block">
              <div className="border-2 border-dashed border-white/20 hover:border-gold/50 transition-colors p-12 text-center cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-3 text-white/60"><Loader2 className="animate-spin" size={28} /><span className="font-arabic">جاري الرفع…</span></div>
                ) : (
                  <div className="flex flex-col items-center gap-3 text-white/60">
                    <Upload size={28} className="text-gold" />
                    <span className="font-arabic">اسحب الملفات هنا أو اضغط للاختيار</span>
                    <span className="text-xs text-white/40">صور / فيديو / PDF — حتى ٢٥ميغابايت لكل ملف</span>
                  </div>
                )}
                <input type="file" multiple onChange={onUpload} className="hidden" accept="image/*,video/*,application/pdf" disabled={uploading} data-testid="media-upload-input" />
              </div>
            </label>
          )}

          {tab === "url" && (
            <form onSubmit={onRegisterUrl} className="space-y-5 max-w-xl" data-testid="media-url-form">
              <label className="block">
                <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">رابط الوسائط</span>
                <input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="editorial-input" dir="ltr" data-testid="media-url-input" />
              </label>
              <label className="block">
                <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">النوع</span>
                <select value={urlKind} onChange={(e) => setUrlKind(e.target.value)} className="bg-ink-soft border border-white/15 px-4 py-3 w-full">
                  <option value="image">صورة</option>
                  <option value="video">فيديو</option>
                  <option value="file">ملف</option>
                </select>
              </label>
              <button type="submit" className="btn-gold"><LinkIcon size={14} />إضافة الرابط</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-5 py-2.5 text-sm font-arabic transition-colors ${active ? "text-gold border-b-2 border-gold -mb-px" : "text-white/55 hover:text-white"}`}
  >
    {children}
  </button>
);

export default MediaLibraryModal;
